import {
  RiArrowGoBackLine,
  RiArrowGoForwardLine,
  RiCloseLine,
} from '@remixicon/react';
import { useEditor } from '@lidojs/design-editor';
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useState,
} from 'react';
import tw from 'twin.macro';
import IconButton from '../IconButton';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-use';
import DiscardModal from '../DiscardModal';
import { ButtonPrimary } from 'libs/design-editor/src/common/button/Button';

interface HeaderLayoutProps {
  openPreview: () => void;
  openSaveModal: () => void;
  loading?: boolean;
}

const HeaderLayout: ForwardRefRenderFunction<
  HTMLDivElement,
  HeaderLayoutProps
> = ({ openSaveModal, loading = false }, ref) => {
  const { t } = useTranslation();

  const { actions, state, query } = useEditor();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const goBack = (force: Boolean = false) => {
    const isContainLayers =
      Object.entries(query.getLayers(state.activePage)).filter(([type, _l]) => {
        return type !== 'ROOT';
      }).length > 0;

    if (isContainLayers && !force) {
      setShowDiscardModal(true);
      return;
    }

    const type = queryParams.get('type') || 'frame';
    const deeplink = queryParams.get('deeplink');
    const campaignUrl = queryParams.get('campaignUrl');
    const targetUrl = campaignUrl
      ? `${import.meta.env.VITE_BASE_TWB}/campaign/${campaignUrl}`
      : `${import.meta.env.VITE_BASE_TWB}/create/${type}?deeplink=${deeplink}`;
    window.location.href = targetUrl;
  };

  return (
    <div
      ref={ref}
      css={(theme) => ({
        height: 55,
        background: theme.colors.background,
        padding: '0px 0px',
        display: 'flex',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.colors.stroke}`,
        alignItems: 'center',
        flexShrink: 0,
      })}
    >
      <DiscardModal
        isOpen={showDiscardModal}
        onClose={() => setShowDiscardModal(false)}
        onConfirm={() => goBack(true)}
      />

      <div
        css={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          flex: '1 1 0',
        })}
      >
        <div
          css={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: 71,
            '@media (max-width: 768px)': {
              width: 54,
            },
          }}
        >
          <IconButton onClick={() => goBack()}>
            <RiCloseLine size={20} />
          </IconButton>
        </div>

        <div
          css={(theme) => ({
            height: '100%',
            width: `1px`,
            background: theme.colors.stroke,
          })}
        />

        <div
          css={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            marginLeft: 8,
          })}
        >
          <IconButton onClick={actions.history.undo}>
            <RiArrowGoBackLine size={16} />
          </IconButton>

          <IconButton onClick={actions.history.redo}>
            <RiArrowGoForwardLine size={16} />
          </IconButton>
        </div>
      </div>

      <div
        css={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          flex: '1 1 0',
          justifyContent: 'flex-end',
        })}
      >
        <ButtonPrimary
          css={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            padding: '8px 14px',
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 14,
            marginLeft: 8,
            marginRight: 16,
          })}
          isLoading={loading}
          onClick={openSaveModal}
        >
          {loading && (
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-50"
                fill="#121212"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          )}

          <span>
            {t('general.button.save')}
          </span>
        </ButtonPrimary>
      </div>
    </div>
  );
};

export default forwardRef(HeaderLayout);
